import React, { useState, useEffect } from 'react';
import '../membership/membership_proposer.css';
import { doc, updateDoc, getFirestore, collection, query, where, getDocs, getDoc,  addDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { getAuth } from 'firebase/auth';
import { ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import SearchableSelect from './searchableSelect';

const ProposerSection = () => {

  const [eligibleMembers, setEligibleMembers] = useState([]);
  const [proposer1, setProposer1] = useState('');
  const [proposer2, setProposer2] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [applicantRealName, setApplicantRealName] = useState('');


  // const db = getFirestore();
  // const auth = getAuth();

  // Fetch members who have been joined for more than 2 years
  useEffect(() => {
  // 1. 🟢 Attach a dynamic observer listener to the Firebase Auth state
  const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
    // If Firebase hasn't resolved the user yet, this callback waits.
    // If no user is logged in at all, we stop loading.
    if (!currentUser) {
      console.log("No user detected yet or user logged out.");
      return; 
    }

    console.log("👤 Auth resolved! Current User UID:", currentUser.uid);
    setLoading(true);

    try {
      // 2. SAFE SELF-FETCH
      try {
        const selfRef = doc(db, "registrations", currentUser.uid);
        const selfSnap = await getDoc(selfRef);
        if (selfSnap.exists()) {
          setApplicantRealName(selfSnap.data().name || "A PArM Applicant");
          console.log("✅ Successfully fetched applicant name:", selfSnap.data().name);
        } else {
          console.warn("⚠️ No registration document found for this UID.");
          setApplicantRealName("A PArM Applicant");
        }
      } catch (selfError) {
        console.error("Non-fatal error fetching applicant profile:", selfError);
        setApplicantRealName("A PArM Applicant"); 
      }

      // 3. FETCH ELIGIBLE PROPOSERS
      console.log("📡 Fetching eligible proposers from database...");
      const q = query(
        collection(db, "registrations"),
        where("status", "==", "verified")
      );

      const querySnapshot = await getDocs(q);
      // console.log("Raw documents fetched from DB total:", querySnapshot.size);

      // const members = querySnapshot.docs
      //   .map(doc => ({ id: doc.id, ...doc.data() }))
      //   .filter(m => m.id !== currentUser.uid); // Use the verified block variable string

      // console.log("Documents left after removing self:", members.length);
      // setEligibleMembers(members);

      const threeYearsAgo = new Date();
      threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);

      const members = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(m => {
          // Exclude current user
          if (m.id === currentUser.uid) return false;

          // Check membership expiry date
          if (!m.membershipExpiry) return false;

          const expiryDate = m.membershipExpiry?.seconds 
            ? new Date(m.membershipExpiry.seconds * 1000) 
            : new Date(m.membershipExpiry);

          // Keep if expiry date is greater than or equal to 3 years ago
          return expiryDate >= threeYearsAgo;
        });

      setEligibleMembers(members);

    } catch (error) {
      console.error("Error fetching proposers:", error);
    } finally {
      setLoading(false);
    }
  });

  // 4. 🟢 CRUCIAL CLEANUP: Unsubscribe from the listener when the page changes
  return () => unsubscribe();
}, [db]); // auth.currentUser removed from dependencies because onAuthStateChanged handles tracking natively

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!proposer1 || !proposer2) return alert("Please select two proposers.");
    if (proposer1 === proposer2) return alert("Please select two different members.");

    setLoading(true);

    try {
      const applicantUid = auth.currentUser.uid;
      // const applicantName = auth.currentUser.displayName;

      const selectedProposers = [proposer1, proposer2];
      const applicantName = applicantRealName;

      // Create a document in 'proposals' collection for each selected proposer
      for (const proposerId of selectedProposers) {
        await addDoc(collection(db, "proposals"), {
          applicantUid: applicantUid,
          // applicantName: applicantName,
          proposerUid: proposerId,
          status: "pending",
          createdAt: new Date(),
        });

        // 2. Extract the target proposer details out of local state
      const proposerDetails = eligibleMembers.find(m => m.id === proposerId);

      if (proposerDetails && proposerDetails.email) {
        
        // 3. 🟢 TRIGGER EMAIL VIA BREVO BY WRITING TO THE "mail" COLLECTION
        await addDoc(collection(db, "mail"), {
          to: proposerDetails.email.trim(),
          from: "malaysiaarborist@gmail.com",
          message: {
            subject: `PArM Membership Proposal Request: ${applicantRealName}`,
            text: `Hello ${proposerDetails.name},\n\n${applicantRealName} has requested you to act as a proposer for their professional PArM membership registration. Please visit the portal at https://www.parm.com.my/login-parm to review their credentials and cast your approval.\n\nThank you,\nMalaysia Society of Arborist (PArM)`,
            html: `
              <div style="font-family: Arial, sans-serif; padding: 20px; color: #333;">
                <h2 style="color: #2e7d32;">Membership Proposal Notification</h2>
                <p>Hello <strong>${proposerDetails.name}</strong>,</p>
                <p><strong>${applicantRealName}</strong> has selected you to act as a proposer for their official PArM membership registration application.</p>
                <p>Please log into your dashboard account and click proposal request to verify their application and finalize your recommendation.</p>
                <br />
                <a href="https://www.parm.com.my/login-parm" style="background-color: #2e7d32; color: white; padding: 10px 20px; text-decoration: none; border-radius: 50px; display: inline-block;">
                  Open Dashboard Portal
                </a>
                <br /><br />
                <hr style="border: none; border-top: 1px solid #eee;" />
                <p style="font-size: 0.85rem; color: #777;">This is an automated notification from the Malaysia Society of Arborist (PArM).</p>
              </div>
            `
          }
        });
        console.log(`Trigger document successfully queued for ${proposerDetails.email}`);
      }
      }

      const userRef = doc(db, "registrations", applicantUid);
      await updateDoc(userRef, {
        status: "pending_proposer", // This matches your dashboard logic
        proposerUids: selectedProposers // Useful for record keeping
      });

      alert("Requests sent successfully! Your proposers will be notified.");
      navigate('/member-dashboard-parm');

      // Redirect or update local state here
    } catch (error) {
      console.error("Error sending requests:", error);
      alert("Failed to send request.");
    }
  };
  

  return (
    <section className="proposer-section">
      <div className="proposer-container">
        <h2 className="proposer-title">Proposer Selection</h2>
        
        <ol className="proposer-instructions">
          <li>Every application for PArM membership must be proposed and supported by a PArM member.</li>
          <li>Please select two PArM members who have been active for more than 2 years.</li>
        </ol>

        <form className="proposer-form" onSubmit={handleSubmit}>
          {/* Proposer 1 */}
          <div className="form-group">
            <label>Proposer 1</label>
            {/* <div className="custom-select">
              <select 
                value={proposer1} 
                onChange={(e) => setProposer1(e.target.value)}
                required
              >
                <option value="" disabled>Select Name</option>
                {eligibleMembers.map(member => (
                  <option key={member.id} value={member.id}>{member.name}</option>
                ))}
              </select>
              <div className="select-arrow">
                <ChevronDown size={20} color="white" strokeWidth={3} />
              </div>
            </div> */}

            {/* Proposer 1 */}
              <SearchableSelect 
                options={eligibleMembers}
                value={proposer1}
                onChange={(selectedId) => setProposer1(selectedId)}
                placeholder="Select or Search Proposer 1"
              />
          </div>

          {/* <div className="form-group">
  <label>Proposer 1</label>
  <input 
    list="members-list" 
    placeholder="Type to search..." 
    className="custom-input" 
    onChange={(e) => {
      
      const selected = eligibleMembers.find(m => m.name === e.target.value);
      if (selected) setProposer1(selected.id);
    }}
  />
  <datalist id="members-list">
    {eligibleMembers.map(member => (
      <option key={member.id} value={member.name} />
    ))}
  </datalist>
</div> */}

          {/* Proposer 2 */}
          <div className="form-group">
            <label>Proposer 2</label>
            {/* <div className="custom-select">
              <select 
                value={proposer2} 
                onChange={(e) => setProposer2(e.target.value)}
                required
              >
                <option value="" disabled>Select Name</option>
                {eligibleMembers.map(member => (
                  <option key={member.id} value={member.id}>{member.name}</option>
                ))}
              </select>
              <div className="select-arrow">
                <ChevronDown size={20} color="white" strokeWidth={3} />
              </div>
            </div> */}

            
              <SearchableSelect 
                options={eligibleMembers}
                value={proposer2}
                onChange={(selectedId) => setProposer2(selectedId)}
                placeholder="Select or Search Proposer 2"
              />
          </div>

          <div className="info-box">
            <span className="bullet">•</span>
            <p>
              Once your selected proposers verify your registration via their dashboard, 
              you will receive an email to proceed with the membership payment.
            </p>
          </div>

          <button type="submit" className="send-request-btn" disabled={loading}>
            {loading ? "Loading Members..." : "Send Request"}
          </button>
        </form>
      </div>
    </section>
  );
};

export default ProposerSection;